import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'localization';
import { SpinnerAtom, useApiClientsOrNull } from 'design-system/my-lib';
import type { RootState } from '@center-pwa/store/store';

interface RoleSphere {
  id: string;
  label: string;
}

const RolesSection: React.FC = () => {
  const { t } = useTranslation();
  const apiClients = useApiClientsOrNull();
  const userId = useSelector((state: RootState) => state.userProfile?.userId);
  const identity = useSelector((state: RootState) => state.userProfile?.identity);

  const orgId = useMemo(() => {
    if (!identity?.users || !userId) return undefined;
    const currentUser = identity.users.find((u: any) => u.id === userId);
    if (!currentUser) return undefined;
    const org = Array.isArray(currentUser.organization)
      ? currentUser.organization[0]
      : currentUser.organization;
    return (org as any)?.id as string | undefined;
  }, [identity, userId]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [roles, setRoles] = useState<RoleSphere[]>([]);

  useEffect(() => {
    if (!apiClients?.graphClient || !orgId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const response = await apiClients.graphClient.post('/search', {
          schemaSystemName: 'center_role',
          limit: 100,
        });
        if (cancelled) return;
        const spheres: any[] = response?.spheres ?? [];
        setRoles(spheres.map((s) => ({ id: s.id, label: s.label })));
      } catch {
        if (cancelled) return;
        setError(t('orgSettings.roles.errors.fetchFailed'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [apiClients, orgId, t]);

  if (loading) {
    return (
      <div className="org-settings-section" role="status">
        <SpinnerAtom size="large" label={t('orgSettings.roles.loading')} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="org-settings-section" role="alert">
        <i className="pi pi-exclamation-triangle" aria-hidden="true" />
        <span>{error}</span>
      </div>
    );
  }

  return (
    <div className="org-settings-section">
      <header className="org-settings-section__header">
        <h2 className="org-settings-section__title">{t('orgSettings.roles.title')}</h2>
        <p className="org-settings-section__subtitle">{t('orgSettings.roles.subtitle')}</p>
      </header>
      {roles.length === 0 ? (
        <div className="org-settings-section__empty">
          <i className="pi pi-shield" aria-hidden="true" />
          <p>{t('orgSettings.roles.empty')}</p>
        </div>
      ) : (
        <ul
          className="org-settings-section__list"
          aria-label={t('orgSettings.roles.listLabel')}
        >
          {roles.map((role) => (
            <li key={role.id} className="org-settings-section__list-item">
              <i className="pi pi-shield" aria-hidden="true" />
              <span>{role.label}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default RolesSection;
